import type { FunnelAggregates } from "@repo/app-types/api/funnel";
import type {
  FunnelId,
  FunnelMoments,
  FunnelStages,
} from "@repo/app-types/entities/funnel";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import { useMemo, useState, type FC } from "react";
import { Heading } from "react-aria-components";
import {
  buildFunnelStageMomentRows,
  computeFunnelMomentShare,
  formatFunnelStageInteractions,
  formatFunnelStageName,
  type FunnelStageMomentRow,
} from "./funnelFormatting.js";
import { FunnelMomentShareBar } from "./funnelMomentShareBar.js";
import {
  FunnelMomentAskButton,
  FunnelMomentSlideover,
} from "./funnelMomentSlideover.js";

export interface FunnelStageMomentsProps {
  funnelId: FunnelId;
  teammateAgentId: TeammateId;
  stages: FunnelStages;
  moments: FunnelMoments;
  aggregates: FunnelAggregates;
  /** Null is before the first stage. */
  stageKey: string | null;
}

/** The agent's moments in the selected stage. */
export const FunnelStageMoments: FC<FunnelStageMomentsProps> = (props) => {
  const { funnelId, teammateAgentId, stages, moments, aggregates, stageKey } =
    props;
  const stageName = formatFunnelStageName(stages, stageKey);
  const stageDescription =
    stageKey === null
      ? undefined
      : stages.find((stage) => stage.key === stageKey)?.description;

  const rows = useMemo(
    () =>
      buildFunnelStageMomentRows({
        stages,
        moments,
        momentCounts: aggregates.moments.agent,
        stageCounts: aggregates.stages.agent,
        stageKey,
      }),
    [stages, moments, aggregates, stageKey],
  );

  return (
    <div className="space-y-5 border-t border-border-light pt-4">
      <div className="space-y-1">
        <Heading level={3} className="text-base font-semibold text-foreground">
          {stageName}
        </Heading>
        {stageDescription ? (
          <p className="text-sm text-muted-foreground">{stageDescription}</p>
        ) : null}
      </div>
      <div aria-live="polite">
        {rows.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-baseline gap-4 px-2">
              <Heading
                level={4}
                className="min-w-0 flex-1 text-xs text-muted-foreground"
              >
                Moments
              </Heading>
              <span className="w-44 shrink-0 text-left text-xs text-muted-foreground sm:w-64">
                Interactions with this moment
              </span>
            </div>
            <ul className="divide-y divide-border-light">
              {rows.map((row) => (
                <li key={row.momentKey}>
                  <FunnelStageMomentRow
                    funnelId={funnelId}
                    teammateAgentId={teammateAgentId}
                    stages={stages}
                    stageKey={stageKey}
                    row={row}
                    topRepShare={computeFunnelMomentShare({
                      momentCounts: aggregates.moments.top_rep,
                      stageCounts: aggregates.stages.top_rep,
                      momentKey: row.momentKey,
                      stageKey,
                    })}
                  />
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No moments in this stage
          </p>
        )}
      </div>
    </div>
  );
};

interface FunnelStageMomentRowProps {
  funnelId: FunnelId;
  teammateAgentId: TeammateId;
  stages: FunnelStages;
  stageKey: string | null;
  row: FunnelStageMomentRow;
  /** The top reps' share of the stage with this moment; null without any. */
  topRepShare: number | null;
}

const FunnelStageMomentRow: FC<FunnelStageMomentRowProps> = (props) => {
  const { stages, stageKey, row } = props;
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <div className="group relative rounded-md focus-within:bg-muted hover:bg-muted">
        <Button
          variant={ButtonVariant.Tertiary}
          className="absolute inset-0 h-full w-full rounded-md focus-visible:outline-2 focus-visible:outline-primary"
          onClick={() => setIsOpen(true)}
        >
          <span className="sr-only">View exchanges for {row.momentName}</span>
        </Button>
        <div className="pointer-events-none relative flex items-center gap-4 px-2 py-3 text-sm">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span>{row.momentName}</span>
            <FunnelMomentAskButton
              momentName={row.momentName}
              stages={stages}
              stageKey={stageKey}
              className="pointer-events-auto -my-1 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
            />
          </div>
          <FunnelMomentShareBar
            momentName={row.momentName}
            share={row.share}
            dropOffRate={row.dropOffRate}
            droppedOffByOutcome={row.droppedOffByOutcome}
            stageInteractions={formatFunnelStageInteractions(stages, stageKey)}
          />
        </div>
      </div>
      <SlideoverContainer
        size={Size.LARGE}
        isDismissable
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      >
        <FunnelMomentSlideover {...props} onAsk={() => setIsOpen(false)} />
      </SlideoverContainer>
    </>
  );
};
